import Head from 'next/head';
import React, { useCallback } from 'react';
import { useLocation } from 'react-use';
import {
    JSON_LD_DEFAULT,
    SEO_DEFAULT,
    FB_FANPAGE,
    DOMAIN_URL,
} from 'src/constants';
import { IMetaSeo } from 'src/interface';
import { ArrayUtils, ImgUtils } from 'src/utils';

interface IProps {
    meta: IMetaSeo;
    jsonLds?: any[];
    isRedirectPage?: boolean;
}

const SEO = ({
    meta = {} as IMetaSeo,
    jsonLds = [JSON_LD_DEFAULT],
    isRedirectPage = false,
}: IProps) => {
    const _meta = Object.assign({}, SEO_DEFAULT, meta);
    const location = useLocation();

    const renderJsonLd = useCallback(() => {
        if (!jsonLds || !jsonLds.length) {
            return null;
        }

        return jsonLds.map((jsonLd, index: number) => {
            return (
                <script
                    key={`jobJSON-${index}`}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(jsonLd),
                    }}
                />
            );
        });
    }, [jsonLds]);

    const { state } = location;
    const { title } = state || {};

    return (
        <Head>
            <title>{title ? title : _meta.title}</title>
            <link rel="canonical" href={_meta.canonicalUrl} />
            <meta name="keywords" content={_meta.keywords} />
            <meta
                name="description"
                content={_meta.description || _meta.title}
            />
            <meta property="fb:app_id" content="128990640458592" />
            <meta
                property="og:title"
                content={
                    isRedirectPage ? DOMAIN_URL + _meta.url : _meta.ogTitle
                }
            />{' '}
            <meta property="og:type" content={_meta.type} />
            <meta property="og:url" content={_meta.ogUrl} />
            <meta
                property="og:image"
                content={
                    isRedirectPage
                        ? SEO_DEFAULT.imageUrl
                        : ImgUtils.convertGifToJpg(_meta.imageUrl)
                }
            />
            <meta property="og:image:width" content={_meta.imageWidth} />
            <meta property="og:image:height" content={_meta.imageHeight} />
            <meta
                property="og:description"
                content={
                    isRedirectPage
                        ? DOMAIN_URL + _meta.url
                        : _meta.description || _meta.title
                }
            />
            <meta property="og:site_name" content={_meta.ogSiteName} />
            <meta property="article:publisher" content={FB_FANPAGE} />
            {_meta.articlePublishedTime && (
                <meta
                    property="article:published_time"
                    content={_meta.articlePublishedTime}
                />
            )}
            {_meta.articleSection && (
                <meta property="article:section" content={_meta.articleSection} />
            )}
            <meta name="robots" content={_meta.robots} />
            {_meta.faviconUrl && (
                <link rel="shortcut icon" href={_meta.faviconUrl} />
            )}
            {!_meta.faviconUrl && (
                <>
                    <link
                        rel="shortcut icon"
                        href="//baomoi-static.bmcdn.me/favicons/favicon.ico"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="57x57"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-57x57-v1.0.1.png"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="60x60"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-60x60-v1.0.1.png"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="72x72"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-72x72-v1.0.1.png"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="76x76"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-76x76-v1.0.1.png"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="114x114"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-114x114-v1.0.1.png"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="120x120"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-120x120-v1.0.1.png"
                    />
                    <link
                        rel="apple-touch-icon"
                        sizes="144x144"
                        href="//baomoi-static.bmcdn.me/favicons/apple-touch-icon-144x144-v1.0.1.png"
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        href="//baomoi-static.bmcdn.me/favicons/favicon-32x32.png"
                        sizes="32x32"
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        href="//baomoi-static.bmcdn.me/favicons/android-chrome-192x192.png"
                        sizes="192x192"
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        href="//baomoi-static.bmcdn.me/favicons/favicon-96x96.png"
                        sizes="96x96"
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        href="//baomoi-static.bmcdn.me/favicons/favicon-16x16.png"
                        sizes="16x16"
                    />
                </>
            )}
            {renderJsonLd()}
        </Head>
    );
};

export default SEO;
